import React, { useState, useMemo } from 'react';
import { useRooms } from '../../context/RoomContext';
import { format, subDays, isWithinInterval, startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, startOfYear, endOfYear } from 'date-fns';
import { DollarSign, Calendar, CreditCard, Hotel } from 'lucide-react';

type DateRange = 'day' | 'week' | 'month' | 'year' | 'all';
type GroupBy = 'date' | 'room';

const AdminAccounts: React.FC = () => {
  const { bookings = [], rooms = [] } = useRooms();
  const [dateRange, setDateRange] = useState<DateRange>('all');
  const [selectedRoom, setSelectedRoom] = useState<string>('all');
  const [groupBy, setGroupBy] = useState<GroupBy>('room');

  const getDateRange = (range: DateRange) => {
    const now = new Date();
    switch (range) {
      case 'day':
        return {
          start: startOfDay(now),
          end: endOfDay(now)
        };
      case 'week':
        return {
          start: startOfWeek(now),
          end: endOfWeek(now)
        };
      case 'month':
        return {
          start: startOfMonth(now),
          end: endOfMonth(now)
        };
      case 'year':
        return {
          start: startOfYear(now),
          end: endOfYear(now)
        };
      default:
        return null;
    }
  };

  const filteredTransactions = useMemo(() => {
    if (!Array.isArray(bookings)) return [];

    const allTransactions = bookings.reduce((acc, booking) => {
      if (!booking || !Array.isArray(booking.payments)) return acc;

      const bookingTransactions = booking.payments.map(payment => {
        if (!payment) return null;
        return {
          ...payment,
          bookingId: booking.id,
          userName: booking.userName || 'Unknown User',
          roomId: booking.roomId,
          roomName: rooms.find(r => r.id === booking.roomId)?.name || 'Unknown Room'
        };
      }).filter(Boolean);

      return [...acc, ...bookingTransactions];
    }, [] as any[]);

    let filtered = allTransactions;

    // Filter by date range
    if (dateRange !== 'all') {
      const range = getDateRange(dateRange);
      if (range) {
        filtered = filtered.filter(transaction => {
          if (!transaction?.date) return false;
          try {
            return isWithinInterval(new Date(transaction.date), range);
          } catch {
            return false;
          }
        });
      }
    }

    // Filter by selected room
    if (selectedRoom !== 'all') {
      filtered = filtered.filter(transaction => transaction.roomId === selectedRoom);
    }

    return filtered;
  }, [bookings, dateRange, selectedRoom, rooms]);

  const groupedTransactions = useMemo(() => {
    if (groupBy === 'room') {
      return filteredTransactions.reduce((acc, transaction) => {
        const key = transaction.roomId;
        if (!acc[key]) {
          acc[key] = {
            transactions: [],
            total: 0,
            roomName: transaction.roomName
          };
        }
        acc[key].transactions.push(transaction);
        acc[key].total += transaction.amount;
        return acc;
      }, {} as Record<string, { transactions: any[], total: number, roomName: string }>);
    }
    return { all: { transactions: filteredTransactions, total: 0, roomName: 'All Rooms' } };
  }, [filteredTransactions, groupBy]);

  const totalAmount = useMemo(() => 
    filteredTransactions.reduce((sum, transaction) => sum + (transaction?.amount || 0), 0)
  , [filteredTransactions]);

  return (
    <div className="px-4 py-6 sm:px-0">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
        <div className="mb-4 sm:mb-0">
          <h2 className="text-xl font-semibold text-gray-800">Financial Transactions</h2>
          <p className="text-sm text-gray-500">View and manage all payment transactions</p>
        </div>

        <div className="flex flex-col sm:flex-row gap-2">
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value as DateRange)}
            className="form-select h-[50px]"
          >
            <option value="all">All Time</option>
            <option value="day">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="year">This Year</option>
          </select>

          <select
            value={selectedRoom}
            onChange={(e) => setSelectedRoom(e.target.value)}
            className="form-select h-[50px]"
          >
            <option value="all">All Rooms</option>
            {rooms.map(room => (
              <option key={room.id} value={room.id}>{room.name}</option>
            ))}
          </select>

          <select
            value={groupBy}
            onChange={(e) => setGroupBy(e.target.value as GroupBy)}
            className="form-select h-[50px]"
          >
            <option value="room">Group by Room</option>
            <option value="date">Show All</option>
          </select>
        </div>
      </div>

      <div className="bg-white p-6 rounded-lg shadow mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Total Collection</h3>
            <p className="text-sm text-gray-500">
              {dateRange === 'all' ? 'All time' : `For ${dateRange}`}
              {selectedRoom !== 'all' && ` - ${rooms.find(r => r.id === selectedRoom)?.name}`}
            </p>
          </div>
          <div className="text-2xl font-bold text-indigo-600">
            ${totalAmount.toFixed(2)}
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {Object.entries(groupedTransactions).map(([roomId, { transactions, total, roomName }]) => (
          <div key={roomId} className="bg-white shadow overflow-hidden sm:rounded-md">
            {groupBy === 'room' && (
              <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Hotel className="h-5 w-5 text-gray-400 mr-2" />
                    <h3 className="text-lg font-medium text-gray-900">{roomName}</h3>
                  </div>
                  <div className="text-lg font-semibold text-indigo-600">
                    ${total.toFixed(2)}
                  </div>
                </div>
              </div>
            )}
            
            <ul className="divide-y divide-gray-200">
              {transactions.length > 0 ? (
                transactions
                  .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                  .map((transaction) => (
                    <li key={transaction.id} className="px-4 py-4 sm:px-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center">
                              <CreditCard className="h-5 w-5 text-indigo-600" />
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              Payment from {transaction.userName}
                            </div>
                            <div className="text-sm text-gray-500">
                              Booking #{transaction.bookingId}
                              {groupBy !== 'room' && ` - ${transaction.roomName}`}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center">
                          <div className="mr-6 text-sm text-gray-500">
                            {format(new Date(transaction.date), 'MMM d, yyyy')}
                          </div>
                          <div className="text-sm font-semibold text-gray-900">
                            ${transaction.amount.toFixed(2)}
                          </div>
                        </div>
                      </div>
                      <div className="mt-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${
                          transaction.method === 'card' 
                            ? 'bg-blue-100 text-blue-800'
                            : transaction.method === 'cash'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-purple-100 text-purple-800'
                        }`}>
                          {transaction.method}
                        </span>
                      </div>
                    </li>
                  ))
              ) : (
                <li className="px-4 py-5 text-center text-gray-500">
                  No transactions found for the selected period
                </li>
              )}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AdminAccounts;